import { CommonEtat } from '@core-lib/enums/affaire-search.enum';
import { AppLanguage } from '@core-lib/enums/app-language.enum';
import { INatureOperation, IQualiteTiers, ITypeOperation } from '@core-lib/models';
import { IDegreRisque, INatureAffaire, INatureAffairePieceJointe } from '@core-lib/models/affaire.model';
import { AutocompleteControlData, IAutocompleteData } from '@core-lib/models/autocomplete.model';
import { IModeReception } from '@core-lib/models/mode-reception.model';
import { IDepartement, IDevise, IListeCodeEconomique, IListesReglementaires, IModeleData, IPays, ISousMotif, ITypeFacteurRisque } from '@core-lib/models/referentials.model';
import { createEntitySliceSelectors } from '@core-lib/store/entity-slice.function';
import { FeatureName } from '@core-lib/store/shared-store/feature';
import { ISharedStoreState } from '@core-lib/store/shared-store/state';
import { MemoizedSelector, createFeatureSelector, createSelector } from '@ngrx/store';

export const selectState: MemoizedSelector<object, ISharedStoreState> = createFeatureSelector<ISharedStoreState>(FeatureName);

export const {
	getEntity: getConfig,
	selectEntity: selectConfig,
	selectIsLoading: selectConfigIsLoading,
	selectError: selectConfigError
} = createEntitySliceSelectors(createSelector(selectState, state => state.config));

export const getIsNumeroDeChequeObligatoire = (state: ISharedStoreState): boolean => getConfig(state.config)?.numeroDeChequeObligatoire;
export const selectIsNumeroDeChequeObligatoire = createSelector(selectState, state => getIsNumeroDeChequeObligatoire(state));
export const getDeviseReference = (state: ISharedStoreState): IDevise => getConfig(state.config)?.deviseReference;
export const selectDeviseReference = createSelector(selectState, state => getDeviseReference(state));

export const getEstPlacementActif = (state: ISharedStoreState): boolean => getConfig(state.config)?.estPlacement;
export const selectEstPlacementActif = createSelector(selectState, state => getEstPlacementActif(state));

export const getNombreDeDecimales = (state: ISharedStoreState): number => getConfig(state.config)?.nombreDeDecimales;
export const selectNombreDeDecimales = createSelector(selectState, state => getNombreDeDecimales(state));

export const getLanguage = (state: ISharedStoreState): AppLanguage => state.language;
export const selectLanguage = createSelector(selectState, state => getLanguage(state));

export const {
	getEntity: getReferentials,
	selectEntity: selectReferentials,
	selectIsLoading: selectReferentialsIsLoading,
	selectError: selectReferentialsError
} = createEntitySliceSelectors(createSelector(selectState, state => state.referentials));

export const getPays = (state: ISharedStoreState): IPays[] => getReferentials(state.referentials)?.pays || [];
export const selectPays = createSelector(selectState, state => getPays(state));

export const getGroupesCodeEconomique = (state: ISharedStoreState): IListeCodeEconomique[] =>
	getReferentials(state.referentials)?.groupesCodeEconomique || [];
export const selectGroupesCodeEconomique = createSelector(selectState, state => getGroupesCodeEconomique(state));

export const getNationalites = (state: ISharedStoreState): IPays[] => getPays(state).filter(p => p.estNationalite);
export const selectNationalites = createSelector(selectState, state => getNationalites(state));

export const getDepartements = (state: ISharedStoreState): IDepartement[] => getReferentials(state.referentials)?.departements || [];
export const selectDepartements = createSelector(selectState, state => getDepartements(state));

export const getDevises = (state: ISharedStoreState): IDevise[] => getReferentials(state.referentials)?.devises || [];
export const selectDevises = createSelector(selectState, state => getDevises(state));

export const getListesReglementairesPays = (state: ISharedStoreState): IListesReglementaires[] => getReferentials(state.referentials)?.listesReglementairePays || [];
export const selectListesReglementairesPays = createSelector(selectState, state => getListesReglementairesPays(state));

export const getAllDevises = (state: ISharedStoreState): IDevise[] => getReferentials(state.referentials)?.allDevises || [];
export const selectAllDevises = createSelector(selectState, state => getAllDevises(state));

export const getDegresRisque = (state: ISharedStoreState): IDegreRisque[] => getReferentials(state.referentials)?.degresRisque || [];
export const selectDegresRisque = createSelector(selectState, state => getDegresRisque(state));

export const getTypesFacteurRisque = (state: ISharedStoreState): ITypeFacteurRisque[] =>
	getReferentials(state.referentials)?.typesFacteurRisque || [];
export const selectTypesFacteurRisque = createSelector(selectState, state => getTypesFacteurRisque(state));

export const getEtatsTraitementNames = (state: ISharedStoreState): CommonEtat[] =>
	getReferentials(state.referentials)?.etatsTraitementNames || [];
export const selectEtatsTraitementNames = createSelector(selectState, state => getEtatsTraitementNames(state));

export const selectNaturesComtpe = createSelector(selectState, state => state?.referentials?.entity?.naturesCompte ?? []);

export const getNaturesAffaire = (state: ISharedStoreState): INatureAffaire[] => getReferentials(state.referentials)?.naturesAffaire || [];
export const selectNaturesAffaire = createSelector(selectState, state => getNaturesAffaire(state));

export const getQualiteTiers = (state: ISharedStoreState): IQualiteTiers[] => getReferentials(state.referentials)?.qualiteTiers || [];
export const selectQualiteTiers = createSelector(selectState, state => getQualiteTiers(state));

export const getModelesData = (state: ISharedStoreState): IModeleData[] => getReferentials(state.referentials)?.modelesData || [];
export const selectModelesData = createSelector(selectState, state => getModelesData(state));

export const getTypesOperation = (state: ISharedStoreState): ITypeOperation[] => getReferentials(state.referentials)?.typesOperation || [];
export const selectTypesOperation = createSelector(selectState, state => getTypesOperation(state));

export const getAutocompleteDataToAdd = (state: ISharedStoreState): AutocompleteControlData => state.autocompleteData;
export const selectAutocompleteDataToAdd = createSelector(selectState, state => getAutocompleteDataToAdd(state));

export const getAutocompleteAddedTiers = (state: ISharedStoreState): IAutocompleteData[] => state.autocompleteAddedTiers;
export const selectAutocompleteAddedTiers = createSelector(selectState, state => getAutocompleteAddedTiers(state));

export const getAutocompleteControlToReset = (state: ISharedStoreState): string[] => state.autocompleteControlToReset;
export const selectAutocompleteControlToReset = createSelector(selectState, state => getAutocompleteControlToReset(state));

export const getTypesDocuments = (state: ISharedStoreState): INatureAffairePieceJointe[] => state.typesDocuments;
export const selectTypesDocuments = createSelector(selectState, state => getTypesDocuments(state));

export const getAllNaturesOperations = (state: ISharedStoreState): INatureOperation[] =>
	getReferentials(state.referentials)?.naturesOperation || [];
export const selectAllNaturesOperation = createSelector(selectState, state => getAllNaturesOperations(state));

export const getAllModesReception = (state: ISharedStoreState): IModeReception[] =>
	getReferentials(state.referentials)?.modesReception || [];
export const selectAllModesReception = createSelector(selectState, state => getAllModesReception(state));

export const getAllSousMotifs = (state: ISharedStoreState): ISousMotif[] => getReferentials(state.referentials)?.sousMotifs || [];
export const selectAllSousMotifs = createSelector(selectState, state => getAllSousMotifs(state));
